import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Card, Button, Tag, Modal } from "antd";
import axios from "axios";
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";
import { ToastContainer, toast } from "react-toastify";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";

dayjs.extend(isSameOrAfter);

dayjs.extend(duration);

function MockInterviewLive() {
  const { id } = useParams();

  const [interviewData, setInterviewData] = useState(null);
  const [loading, setLoading] = useState(true);

  const [cancelModalOpen, setCancelModalOpen] = useState(false);
const [cancelInput, setCancelInput] = useState("");
const [cancelLoading, setCancelLoading] = useState(false);

  const token = localStorage.getItem("token");

  /* ================= FETCH INTERVIEW ================= */

  useEffect(() => {
    fetchInterview();
  }, [id]);

  const fetchInterview = async () => {
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/mock-interview/get-single-interview`,
        { id },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (res.data.success) {
        setInterviewData(res.data.data);
      }

      setLoading(false);
    } catch {
      setLoading(false);
    }
  };

  /* ================= LOCAL STATES ================= */

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [availability, setAvailability] = useState([]);

  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState("PM");

  const previousDate = interviewData?.ScheduledDate;
  const previousTime = interviewData?.ScheduledTime;

  /* ================= FETCH AVAILABILITY ================= */

  const fetchAvailability = async () => {
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/mock-interview/instructor-availability-dates-for-student`,
        {
          InstructorEmail: interviewData?.InstructorEmail,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (res.data.success) {
        setAvailability(res.data.data);
      }
    } catch {
      toast.error("Failed to load availability");
    }
  };

  useEffect(() => {
    if (interviewData?.InstructorEmail) {
      fetchAvailability();
    }
  }, [interviewData]);

  /* ================= RESCHEDULE ================= */

  const handleReschedule = async () => {
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/mock-interview/reschedule-mock-interview`,
        {
          id: interviewData.id,
          InstructorEmail: interviewData.InstructorEmail,
          ScheduledDate: dayjs(selectedDate).format("YYYY-MM-DD"),
          ScheduledTime: selectedTime,
          PreviousSlotDate: dayjs(previousDate).format("YYYY-MM-DD"),
          PreviousSlotTime: previousTime,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (res.data.success) {
        toast.success("Interview Rescheduled");

        setInterviewData((prev) => ({
          ...prev,
          ScheduledDate: dayjs(selectedDate).format("YYYY-MM-DD"),
          ScheduledTime: selectedTime,
          InterviewStatus: "RESCHEDULED",
        }));

        setIsModalOpen(false);
        fetchInterview();
      }
    } catch {
      toast.error("Reschedule failed");
    }
  };

  /* ================= GROUPED ================= */

  const grouped = {};
  availability.forEach((item) => {
    if (!grouped[item.AvailableDate]) {
      grouped[item.AvailableDate] = [];
    }

    if (Array.isArray(item.AvailableTime)) {
      grouped[item.AvailableDate].push(...item.AvailableTime);
    } else if (item.AvailableTime) {
      grouped[item.AvailableDate].push(item.AvailableTime);
    }
  });

  /* ================= MODAL OPEN ================= */

  const openModal = () => {
    setIsModalOpen(true);
    setSelectedDate(null);
    setSelectedPeriod("PM");
    setSelectedTime(null);
    fetchAvailability();
  };

  /* ================= TIMER ================= */

  const [timeLeft, setTimeLeft] = useState("");

  useEffect(() => {
    if (!interviewData?.ScheduledDate || !interviewData?.ScheduledTime) return;

    const interval = setInterval(() => {
      const interviewDateTime = dayjs(
        `${interviewData.ScheduledDate} ${interviewData.ScheduledTime}`
      );

      const now = dayjs();
      const diff = interviewDateTime.diff(now);

      if (diff <= 0) {
        setTimeLeft("Started");
        clearInterval(interval);
        return;
      }

      const d = dayjs.duration(diff);

      setTimeLeft(
        `${Math.floor(d.asDays())}d ${String(d.hours()).padStart(
          2,
          "0"
        )}h ${String(d.minutes()).padStart(2, "0")}m ${String(
          d.seconds()
        ).padStart(2, "0")}s`
      );
    }, 1000);

    return () => clearInterval(interval);
  }, [interviewData]);

  /* ================= LOADING ================= */

  if (loading) return <div>Loading...</div>;

 if (!interviewData)
  return (
    <div className="flex items-center justify-center min-h-[60vh]">

      <div className="max-w-md p-6 text-center border border-red-200 shadow-sm rounded-2xl bg-gradient-to-br from-red-50 to-white">

        {/* ICON */}
        <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 text-red-600 bg-red-100 rounded-full">
          ⚠️
        </div>

        {/* TITLE */}
        <h2 className="text-2xl font-bold text-red-600">
          Unauthorized Access
        </h2>

        {/* MESSAGE */}
        <p className="mt-2 text-sm leading-relaxed text-slate-600">
          You do not have permission to view this interview data. 
          This resource may be restricted or no longer available.
        </p>

        {/* SUBTEXT */}
        <p className="mt-1 text-xs text-slate-500">
          Please contact support or navigate back to your dashboard.
        </p>

        {/* ACTION */}
        <div className="mt-5">
          <button
            onClick={() => window.history.back()}
            className="px-5 py-2 text-sm font-semibold text-white transition-all duration-200 shadow-md rounded-xl bg-gradient-to-r from-red-500 to-rose-600 hover:from-red-600 hover:to-rose-700"
          >
            ← Go Back
          </button>
        </div>

      </div>

    </div>
  );


  const handleCancelInterview = async () => {
  if (cancelInput.toLowerCase() !== "cancel") {
    toast.error('Type "cancel" to confirm');
    return;
  }

  try {
    setCancelLoading(true);

    const res = await axios.post(
      `${process.env.REACT_APP_API_URL}/mock-interview/cancel-mock-interview`,
      {
        id: interviewData.id
      },
      {
        headers: { Authorization: `Bearer ${token}` }
      }
    );

    if (res.data.success) {
      toast.success("Interview cancelled");

      setCancelModalOpen(false);

      // 👉 Option 1: go back
      window.history.back();

      // 👉 Option 2 (better UX):
      // navigate("/student/dashboard");
    }

  } catch (err) {
    toast.error(err?.response?.data?.message || "Cancel failed");
  } finally {
    setCancelLoading(false);
  }
};


const today = dayjs().startOf("day"); // today 00:00 (IST)
const interviewDate = dayjs(interviewData?.ScheduledDate).startOf("day");

// condition
const canJoinInterview =
  interviewData?.MockInterviewLink &&
  interviewDate.isSameOrAfter(today);

  /* ================= UI ================= */

  return (
    <div className="min-h-screen p-10 bg-slate-50">
      <div className="max-w-6xl mx-auto space-y-6">
        <ToastContainer
                           position="bottom-left"
                           autoClose={5000}
                           hideProgressBar={true}
                           newestOnTop={false}
                           closeOnClick
                           rtl={false}
                           pauseOnFocusLoss
                           draggable
                           pauseOnHover
                           theme="dark"
                           className="!mb-6 !ml-4"
                           toastClassName="!inline-flex !items-center !whitespace-nowrap !bg-black !text-white !px-4 !py-2 !rounded-lg !w-auto !min-w-0 !shadow-lg"
                         />

        <Card className="relative p-6 text-center transition-all bg-white border shadow-sm rounded-2xl hover:shadow-md">

  {/* STATUS TAG */}
  <div className="absolute top-4 right-4">
    <Tag color="blue" className="px-3 py-1 font-semibold rounded-full">
      {interviewData.InterviewStatus}
    </Tag>
  </div>

  {/* TITLE */}
  <h2 className="mt-2 text-2xl font-bold text-slate-800">
    Upcoming Interview
  </h2>

  {/* OPTIONAL SUBTEXT */}
  <p className="mt-1 text-sm text-slate-500">
    Manage your scheduled interview actions
  </p>

  {interviewData?.InterviewStatus === "NO_SHOW" && (

  <div className="p-4 border border-red-200 shadow-sm rounded-xl bg-red-50">

    <div className="flex items-start gap-3">

      {/* ICON */}
      <div className="flex items-center justify-center w-10 h-10 text-red-600 bg-red-100 rounded-full">
        ⚠️
      </div>

      {/* CONTENT */}
      <div>
        <h3 className="text-sm font-semibold text-red-700">
          Interview Marked as No Show
        </h3>

        <p className="mt-1 text-sm leading-relaxed text-red-600">
          This interview has been marked as <b>NO_SHOW</b>, indicating that the student did not attend the scheduled session. 
          Further actions such as rescheduling or joining are no longer available for this interview.
        </p>

        <p className="mt-2 text-xs text-red-500">
          If this was marked incorrectly, please contact the administrator or support team.
        </p>
      </div>

    </div>

  </div>

)}

  {/* ACTION BUTTONS */}
  <div className="flex flex-wrap justify-center gap-4 mt-6">

   {canJoinInterview ? (
  <Button
    type="primary"
    size="large"
    onClick={() =>
      window.open(interviewData.MockInterviewLink, "_blank")
    }
    className="px-6 font-semibold text-white transition-all duration-200 border-none shadow-md h-11 rounded-xl bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700"
  >
    🚀 Join Interview
  </Button>
) : (
  <Button
    size="large"
    disabled
    className="px-6 font-semibold border-none cursor-not-allowed text-slate-400 bg-slate-200 h-11 rounded-xl"
  >
    Interview Expired
  </Button>
)}

   {interviewData.InterviewStatus !== "NO_SHOW" && (
  <Button
    danger
    size="large"
    className="px-6 font-semibold transition-all duration-200 shadow-sm h-11 rounded-xl"
    onClick={() => setCancelModalOpen(true)}
  >
    ❌ Cancel
  </Button>
)}

{interviewData.InterviewStatus !== "NO_SHOW" && (
    <Button
      size="large"
      onClick={openModal}
      className="px-6 font-semibold transition-all duration-200 border shadow-sm h-11 rounded-xl hover:bg-slate-50"
    >
      🔄 Reschedule
    </Button>
)}

  </div>

</Card>

         <Card className="transition duration-300 border border-black rounded-lg shadow-sm bg-slate-200 hover:shadow-md">
        
          <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
        
            {/* INSTRUCTOR */}
            <div className="flex items-center gap-3">
              <div className="p-2 text-blue-600 bg-blue-100 rounded-lg">
                👨‍🏫
              </div>
              <div>
                <p className="text-xs text-slate-700">Instructor</p>
                <p className="font-semibold text-slate-800">
                  {interviewData.InstructorName}
                </p>
              </div>
            </div>
        
            {/* DATE */}
            <div className="flex items-center gap-3">
              <div className="p-2 text-purple-600 bg-purple-100 rounded-lg">
                📅
              </div>
              <div>
                <p className="text-xs text-slate-700">Date</p>
                <p className="font-semibold text-slate-800">
                  {dayjs(interviewData.ScheduledDate).format("DD MMM YYYY")}
                </p>
              </div>
            </div>
        
           {/* TIME */}
        <div className="flex items-center gap-3">
          <div className="p-2 text-green-600 bg-green-100 rounded-lg">
            ⏰
          </div>
          <div>
            <p className="text-xs text-slate-700">Time</p>
            <p className="font-semibold text-slate-800">
              {dayjs(interviewData.ScheduledTime, "HH:mm").format("hh:mm A")}
            </p>
          </div>
        </div>
        
          </div>
        
        </Card>
        
              <Card className="mb-10 border border-blue-200 rounded-lg shadow-sm bg-gradient-to-r from-blue-50 to-blue-100">
        
          <h2 className="mb-4 text-xl font-bold text-blue-800">
            📌 Mock Interview Guidelines
          </h2>
        
          <div className="grid gap-6 text-sm md:grid-cols-2 text-slate-700">
        
            {/* LEFT */}
            <div className="space-y-3">
        
              <div className="flex gap-2">
                <span>🎯</span>
                <p>
                  Attend your interview on time. Late entry may lead to cancellation.
                </p>
              </div>
        
              <div className="flex gap-2">
                <span>💻</span>
                <p>
                  Ensure stable internet connection and working microphone.
                </p>
              </div>
        
              <div className="flex gap-2">
                <span>📚</span>
                <p>
                  Revise key concepts before attending the interview.
                </p>
              </div>
        
            </div>
        
            {/* RIGHT */}
            <div className="space-y-3">
        
              <div className="flex gap-2">
                <span>📅</span>
                <p>
                  You can reschedule or cancel before the scheduled time.
                </p>
              </div>
        
              <div className="flex gap-2">
                <span>🎥</span>
                <p>
                  Join using the provided interview link at the scheduled time.
                </p>
              </div>
        
              <div className="flex gap-2">
                <span>🚀</span>
                <p>
                  Perform confidently — this simulates a real job interview.
                </p>
              </div>
        
            </div>
        
          </div>
        
        </Card>
        
        <Modal
  title={
    <div className="text-xl font-bold text-slate-800">
      Reschedule Interview
    </div>
  }
  open={isModalOpen}
  onCancel={() => setIsModalOpen(false)}
  footer={null}
  width={720}
  centered
>

{/* ================= HEADER INFO ================= */}
<div className="mt-2 mb-6 space-y-4">

  {/* Instructor */}
  <div className="p-4 border border-blue-200 shadow-sm bg-blue-50 rounded-xl">
    <p className="text-xs text-slate-600">Instructor</p>
    <p className="text-lg font-semibold text-blue-800">
      {interviewData.InstructorName}
    </p>
  </div>

  {/* Previous Slot */}
  <div className="p-4 border border-yellow-200 shadow-sm bg-yellow-50 rounded-xl">
    <p className="text-xs text-slate-600">Current Scheduled Slot</p>
    <p className="font-semibold text-yellow-800">
      {dayjs(interviewData.ScheduledDate).format("DD MMM YYYY")} •{" "}
      {dayjs(interviewData.ScheduledTime, ["HH:mm", "HH:mm:ss"]).format("hh:mm A")}
    </p>
  </div>

  {/* NOTE */}
  <div className="p-4 border shadow-sm bg-slate-50 rounded-xl border-slate-200">
    <p className="text-sm text-slate-700">
      ⚠️ Select a new date and time based on instructor availability. 
      Rescheduling is allowed only before the scheduled time.
    </p>
  </div>

</div>

{/* ================= NO AVAILABILITY ================= */}
{Object.keys(grouped).length === 0 ? (

  <div className="flex flex-col items-center justify-center py-12 text-center">
    <p className="text-base font-medium text-slate-600">
      No dates available for this instructor
    </p>
  </div>

) : (

  <>
    {/* ================= DATE ================= */}
    <div className="flex gap-3 p-4 mb-4 overflow-x-auto rounded-xl bg-slate-100">

      {Object.keys(grouped).map((date) => (
        <div
          key={date}
          onClick={() => {
            setSelectedDate(date);
            setSelectedPeriod(null);
            setSelectedTime(null);
          }}
          className={`cursor-pointer min-w-[90px] text-center p-3 rounded-xl transition-all duration-200 ${
            selectedDate === date
              ? "bg-gradient-to-r from-blue-500 to-indigo-600 text-white shadow-md"
              : "bg-white border border-slate-200 hover:bg-slate-50"
          }`}
        >
          <p className="text-xs">{dayjs(date).format("ddd")}</p>
          <h2 className="text-xl font-bold">
            {dayjs(date).format("DD")}
          </h2>
          <p className="text-xs">{dayjs(date).format("MMM")}</p>
        </div>
      ))}

    </div>

    {/* ================= AM PM ================= */}
    {selectedDate && (
      <div className="flex gap-3 p-4 mb-4 rounded-xl bg-slate-100">

        {["AM", "PM"].map((p) => (
          <button
            key={p}
            onClick={() => {
              setSelectedPeriod(p);
              setSelectedTime(null);
            }}
            className={`px-5 py-2 rounded-xl font-semibold transition-all duration-200 ${
              selectedPeriod === p
                ? "bg-gradient-to-r from-blue-500 to-indigo-600 text-white shadow"
                : "bg-white border border-slate-200 hover:bg-slate-50"
            }`}
          >
            {p}
          </button>
        ))}

      </div>
    )}

    {/* ================= TIME ================= */}
    {selectedDate && selectedPeriod && (
      <div className="flex flex-wrap gap-3 p-4 rounded-xl bg-slate-100">

        {(grouped[selectedDate] || [])
          .filter((time) => {
            const hour = parseInt(time.split(":")[0]);
            return selectedPeriod === "AM"
              ? hour < 12
              : hour >= 12;
          })
          .map((time, index) => (

            <button
              key={index}
              onClick={() => setSelectedTime(time)}
              className={`px-4 py-2 rounded-xl border font-medium transition-all duration-200 ${
                selectedTime === time
                  ? "bg-gradient-to-r from-blue-500 to-indigo-600 text-white shadow"
                  : "bg-white border-slate-200 hover:bg-slate-50"
              }`}
            >
              {dayjs(time, ["HH:mm", "HH:mm:ss"]).format("hh:mm A")}
            </button>

          ))}

        {/* No slots */}
        {(grouped[selectedDate] || []).filter((time) => {
          const hour = parseInt(time.split(":")[0]);
          return selectedPeriod === "AM"
            ? hour < 12
            : hour >= 12;
        }).length === 0 && (
          <p className="text-sm text-slate-500">
            No slots available for this period
          </p>
        )}

      </div>
    )}

    {/* ================= BUTTON ================= */}
    {selectedDate && selectedPeriod && selectedTime && (
      <div className="mt-6 text-center">

        <Button
          type="primary"
          size="large"
          onClick={handleReschedule}
          className="h-12 px-8 text-base font-semibold text-white transition-all duration-200 border-none shadow-md rounded-xl bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700"
        >
          🔄 Confirm Reschedule
        </Button>

      </div>
    )}
  </>
)}

</Modal>

        <Modal
  title={
    <div className="text-xl font-bold text-red-600">
      Cancel Interview
    </div>
  }
  open={cancelModalOpen}
  onCancel={() => {
    setCancelModalOpen(false);
    setCancelInput("");
  }}
  footer={null}
  centered
>

<div className="mt-3 space-y-5">

  {/* WARNING CARD */}
  <div className="p-4 border border-red-200 shadow-sm bg-red-50 rounded-xl">
    <p className="text-sm font-medium text-red-700">
      ⚠️ This action is irreversible. Once cancelled, this interview slot cannot be recovered.
    </p>
  </div>

  {/* INSTRUCTION */}
  <div>
    <p className="text-sm text-slate-600">
      To confirm, type{" "}
      <span className="font-semibold text-red-600">cancel</span> below
    </p>
  </div>

  {/* INPUT */}
  <input
    type="text"
    value={cancelInput}
    onChange={(e) => setCancelInput(e.target.value)}
    placeholder="Type 'cancel' to confirm..."
    className="w-full px-3 transition border rounded-lg outline-none h-11 border-slate-300 focus:ring-2 focus:ring-red-400 focus:border-red-400"
  />

  {/* ACTION BUTTON */}
  <Button
    danger
    type="primary"
    block
    loading={cancelLoading}
    onClick={handleCancelInterview}
    className="h-12 text-base font-semibold text-white transition-all duration-200 border-none shadow-md rounded-xl bg-gradient-to-r from-red-500 to-rose-600 hover:from-red-600 hover:to-rose-700"
  >
    ❌ Confirm Cancellation
  </Button>

</div>

</Modal>
      </div>
    </div>
  );
}

export default MockInterviewLive;